import { DateTime } from "luxon";

import { ClockIcon } from "@heroicons/react/24/outline";

import { IProductionManagerBayVehicle } from "../../models/ProductionManager";
import classNames from "../../utils/classNames";

interface AssemblyTimerProps {
  vehicle: IProductionManagerBayVehicle;
  now: DateTime;
  size?: "sm" | "md";
}

const formatDuration = (totalSeconds: number): string => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
};

// Live timer for the assembly a vehicle is currently on. Elapsed time is the accumulated
// worked time from completed (paused) session segments plus, if a session is currently
// running, the time since it started. It flags red once it passes the assembly's target.
export default function AssemblyTimer({ vehicle, now, size = "md" }: AssemblyTimerProps) {
  const { currentAssemblyElapsedSeconds, currentAssemblyStartedOn, currentAssemblyAverageMinutes } =
    vehicle;

  const runningSeconds = currentAssemblyStartedOn
    ? Math.max(
        0,
        now.diff(DateTime.fromISO(currentAssemblyStartedOn, { zone: "utc" }), "seconds").seconds
      )
    : 0;

  const elapsedSeconds = (currentAssemblyElapsedSeconds ?? 0) + runningSeconds;

  // Nothing has been timed for this assembly yet — no session recorded.
  if (elapsedSeconds <= 0) return null;

  const isRunning = !!currentAssemblyStartedOn;
  const targetSeconds = currentAssemblyAverageMinutes ? currentAssemblyAverageMinutes * 60 : null;
  const isOver = targetSeconds !== null && elapsedSeconds > targetSeconds;

  return (
    <div
      className={classNames(
        "inline-flex items-center gap-1 rounded font-medium tabular-nums",
        size === "sm" ? "text-[10px] px-1 py-0.5" : "text-[11px] px-1.5 py-0.5",
        isOver
          ? "bg-red-50 text-red-700 border border-red-200"
          : isRunning
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
            : "bg-gray-50 text-gray-600 border border-gray-200"
      )}
      title={
        targetSeconds !== null
          ? `Target ${currentAssemblyAverageMinutes}m${isOver ? " — running over" : ""}`
          : "No target set for this assembly"
      }
    >
      <ClockIcon className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} />
      <span>{formatDuration(elapsedSeconds)}</span>
      {currentAssemblyAverageMinutes ? (
        <span className={isOver ? "text-red-500" : "text-gray-400"}>
          / {currentAssemblyAverageMinutes}m
        </span>
      ) : null}
    </div>
  );
}
